import { DataTable } from '@/components/DataTable/DataTable';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { useGet } from '@/query/fetchHooks';
import { newUid, useMobile, nthLastIndexOf } from '@/util';
import MobileSubPageMenu from '@/components/HeaderMobile/MobileSubPageMenu';
import { Userwithorganisationwithidps } from '@/models';
import {
  InputText,
  PersonAvatar,
  TEOLoader,
  PageHeader,
} from '@teo/components';
import { Search } from '@teo/components/icons';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { BreadCrumbsDetails } from '@/components/BreadCrumbsDetails';
import { NavContext } from '@/components/NavContext';

function FollowUpProfileCompetencies() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { organisationId, professionId } = useParams();
  const { setSubPage } = useContext(NavContext);
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;
  const canSeeAllMembers =
    Math.max(authUser?.role || 0, authUser?.activeOrganisationRole || 0) >= 90;

  const [profile, setProfile] = useState<any>(null);
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [loadTable, setLoadTable] = useState(true);
  const [refetchTable, setRefetchTable] = useState(newUid(20));

  const basePath = window.location.pathname;
  const userPath =
    basePath.slice(0, basePath.lastIndexOf('/profiles')) + '/users';
  const title = profile?.professionName
    ? t('pages.follow_up_users.profile_details_title', {
        profile: profile.professionName,
      })
    : t('pages.follow_up_users.profiles_tab');

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedFilter(filter), 250);
    return () => clearTimeout(timeout);
  }, [filter]);

  useEffect(() => {
    setSubPage(true);
    return () => setSubPage(false);
  }, []);

  useEffect(() => {
    setLoadTable(true);
    setRefetchTable(newUid(20));
  }, [professionId]);

  useGet('/organisationprofessiondetails', {
    organisationId,
    professionId,
    count: 1,
    enabled: !!organisationId && !!professionId,
    onSuccess: (result: any) => {
      const rows = result?.data?.data || result?.data || [];
      setProfile(rows?.[0] || null);
    },
  });

  const formatPercent = (value: any) => {
    const number = Number(value || 0);
    return `${Math.round(number * 100)}%`;
  };

  const progressBar = (value: any) => {
    const progress = Math.max(0, Math.min(1, Number(value || 0)));

    return (
      <div className="flex min-w-[180px] items-center gap-2">
        <div className="h-2 flex-1 rounded-full bg-[#E9E9EB]">
          <div
            className={`h-2 rounded-full ${
              progress >= 0.8 ? 'bg-[#24A148]' : 'bg-[#F1C21B]'
            }`}
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
        <span className="min-w-[38px] text-right text-xs font-semibold tabular-nums">
          {formatPercent(progress)}
        </span>
      </div>
    );
  };

  const columns: any = [
    {
      title: t('columns.username'),
      id: 'username',
      className: 'col-start-1 col-end-12 row-start-1',
      render: (user: any) => (
        <div className="flex items-center">
          <PersonAvatar
            inline
            id={user.userId}
            name={user.username || ''}
            imageSrc={user.picture || undefined}
          />
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-[12px] font-medium text-[#231f20] sm:text-sm">
              {user.username}
            </span>
            <span className="truncate text-xs font-normal text-[#7D7D81]">
              {user.email}
            </span>
          </div>
        </div>
      ),
    },
    {
      addTitleOnMobile: true,
      className: 'col-start-1 col-end-12 row-start-2',
      id: 'progress',
      title: t('pages.follow_up_users.coverage'),
      align: 'right',
      render: (user: any) => (
        <div className="flex flex-col items-end gap-1">
          {progressBar(user.progress)}
          <span className="text-xs text-[#7D7D81]">
            {user.completedCompetences || 0}/{user.requiredCompetences || 0}
          </span>
        </div>
      ),
    },
  ];

  const baseProfPath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 2)
  );

  return (
    <div className="max-w-6xl pb-8">
      {isMobile && (
        <MobileSubPageMenu
          backFn={() => navigate(`${baseProfPath + '?tab=profiles'}`)}
        />
      )}
      {!loadTable && (
        <>
          <BreadCrumbsDetails
            prevPathname={baseProfPath + '?tab=profiles'}
            prevName={t('breadcrumbs.PROFILES')}
            pathname={window.location.pathname}
            name={profile?.professionName}
          />
          <div className="pb-2 md:pt-6">
            {isMobile ? (
              <div className="flex w-full flex-col gap-4">
                <div className="flex items-center">
                  <h1 className="break-all text-lg font-semibold text-black sm:text-xl">
                    {profile?.professionName}
                  </h1>
                </div>
              </div>
            ) : (
              <PageHeader
                to={baseProfPath + '?tab=profiles'}
                ariaLabel={'placeholder'}
              >
                <div className="flex w-full items-center">
                  <div className="mx-2 flex w-full flex-col justify-between gap-4 xl:mx-4 xl:flex-row">
                    <div className="flex flex-col content-center">
                      <h1 className="break-all text-lg font-semibold text-black sm:text-xl">
                        {profile?.professionName}
                      </h1>
                    </div>
                  </div>
                </div>
              </PageHeader>
            )}
          </div>
        </>
      )}
      {loadTable && (
        <div className="mx-auto flex min-h-[60vh] w-full justify-center py-20 px-10">
          <TEOLoader loaderSrc="/images/TEOloader.gif" className="w-[110px]" />
        </div>
      )}
      <Fragment>
        <div className={`${loadTable && 'hidden'}`}>
          <div className="py-5 md:w-1/2">
            <InputText
              value={filter}
              onChange={(ev: any) => setFilter(ev.target.value)}
              Icon={Search}
              placeholder={t('pages.follow_up_users.search_profile_user')}
            />
          </div>
          <DataTable
            key={refetchTable}
            filter={debouncedFilter}
            rowPath={(user) => `${baseProfPath}/user/${user.userId}`}
            defaultSort={[{ id: 'username', desc: false }]}
            fetch={useGet.bind(null, '/organisationstudentprofessiondetails', {
              organisationId,
              professionId,
              userId: canSeeAllMembers ? undefined : authUser?.id,
              onSuccess: (result: any) => {
                const rows = result?.data?.data || result?.data || [];

                if (!profile?.professionName && rows?.[0]?.professionName) {
                  setProfile(rows[0]);
                }

                setLoadTable(false);
              },
            })}
            empty={{
              template: 1,
              text: t('empty_tenplate.user_list'),
            }}
            columns={columns}
          />
        </div>
      </Fragment>
    </div>
  );
}

export default FollowUpProfileCompetencies;
